import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/Button';
import { SegmentedControl } from '@/shared/ui/SegmentedControl';
import { Switch } from '@/shared/ui/Switch';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import { useShellStore } from '@/features/shell/model/shellStore';
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES } from '@/shared/i18n';
import type { Preferences } from '@/entities/preferences/model/schema';
import { FONT_CHOICE_KEYS, FONT_LABELS, fontStackOf, type FontChoice } from '@/entities/preferences/lib/fontFamily';
import { AccentPicker } from './AccentPicker';
import { SoundSettings } from './SoundSettings';

function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-medium">{label}</span>
        {hint ? <span className="text-xs text-fg-muted">{hint}</span> : null}
      </span>
      {children}
    </div>
  );
}

/**
 * Kullanıcı bazlı ayarlar: menü konumu, tema, vurgu rengi, yazı tipi/boyutu, yoğunluk,
 * dil, ana sayfa, sekme geri yükleme.
 */
export function SettingsDialog() {
  const { t } = useTranslation();
  const open = useShellStore((s) => s.settingsOpen);
  const setOpen = useShellStore((s) => s.setSettings);
  const prefs = usePrefsStore((s) => s.prefs);
  const set = usePrefsStore((s) => s.set);
  const reset = usePrefsStore((s) => s.reset);

  return (
    <Dialog open={open} onOpenChange={setOpen} title={t('prefs.title')} footer={<Button variant="secondary" onClick={reset}>{t('prefs.reset')}</Button>}>
      <div className="divide-y divide-border">
        <Row label={t('prefs.menuPosition.label')}>
          <SegmentedControl<Preferences['menuPosition']>
            value={prefs.menuPosition}
            onChange={(v) => set({ menuPosition: v })}
            options={[
              { value: 'sidebar', label: t('prefs.menuPosition.sidebar') },
              { value: 'top', label: t('prefs.menuPosition.top') },
              { value: 'rail', label: t('prefs.menuPosition.rail') },
            ]}
          />
        </Row>
        <Row label={t('prefs.theme.label')}>
          <SegmentedControl<Preferences['theme']>
            value={prefs.theme}
            onChange={(v) => set({ theme: v })}
            options={[
              { value: 'light', label: t('prefs.theme.light') },
              { value: 'dark', label: t('prefs.theme.dark') },
              { value: 'system', label: t('prefs.theme.system') },
            ]}
          />
        </Row>
        <Row label={t('prefs.accent.label')}>
          <AccentPicker value={prefs.accentColor} onChange={(accentColor) => set({ accentColor })} />
        </Row>
        <Row label={t('prefs.fontFamily.label')}>
          <select
            value={prefs.fontFamily}
            onChange={(e) => set({ fontFamily: e.target.value as Preferences['fontFamily'] })}
            className="h-[var(--control-h)] rounded-md border border-border bg-bg-elevated px-2 text-sm"
            aria-label={t('prefs.fontFamily.label')}
          >
            {FONT_CHOICE_KEYS.map((k) => (
              // Seçenek kendi yazı tipiyle gösterilir: listeden seçmeden önce görünsün.
              // Font adları çevrilmez; yalnızca "Varsayılan" ve "Sistem" çeviriden gelir.
              <option key={k} value={k} style={{ fontFamily: fontStackOf(k as FontChoice) }}>
                {FONT_LABELS[k] ?? t(`prefs.fontFamily.${k}`)}
              </option>
            ))}
          </select>
        </Row>
        <Row label={t('prefs.fontScale.label')}>
          <SegmentedControl<Preferences['fontScale']>
            value={prefs.fontScale}
            onChange={(v) => set({ fontScale: v })}
            options={[
              { value: 0.875, label: t('prefs.fontScale.s') },
              { value: 1, label: t('prefs.fontScale.m') },
              { value: 1.125, label: t('prefs.fontScale.l') },
              { value: 1.25, label: t('prefs.fontScale.xl') },
            ]}
          />
        </Row>
        <Row label={t('prefs.density.label')}>
          <SegmentedControl<Preferences['density']>
            value={prefs.density}
            onChange={(v) => set({ density: v })}
            options={[
              { value: 'compact', label: t('prefs.density.compact') },
              { value: 'comfortable', label: t('prefs.density.comfortable') },
            ]}
          />
        </Row>
        <Row label={t('prefs.language.label')}>
          <select
            value={prefs.language}
            onChange={(e) => set({ language: e.target.value as Preferences['language'] })}
            className="h-[var(--control-h)] rounded-md border border-border bg-bg-elevated px-2 text-sm"
          >
            {SUPPORTED_LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {LANGUAGE_LABELS[l]}
              </option>
            ))}
          </select>
        </Row>
        <Row label={t('prefs.homeLayout.label')}>
          <SegmentedControl<Preferences['homeLayout']>
            value={prefs.homeLayout}
            onChange={(v) => set({ homeLayout: v })}
            options={[
              { value: 'widgets', label: t('prefs.homeLayout.widgets') },
              { value: 'simple', label: t('prefs.homeLayout.simple') },
            ]}
          />
        </Row>
        <Row label={t('prefs.tabsRestore.label')}>
          <Switch checked={prefs.tabsRestore} onCheckedChange={(v) => set({ tabsRestore: v })} />
        </Row>
        {/* Cihaz adı satırı kaldırıldı: ad artık bilgisayardaki Octo.Agent'tan geliyor (Cihazlar). */}
        <SoundSettings Row={Row} />
      </div>
    </Dialog>
  );
}
